import cn from 'classnames';
import type { CommentableContentObject, Reply } from 'models';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, type RichTextMentions } from 'ui';
import { CommentThread } from '../comment-thread';
import { useCommentDisplayInitialCount } from './hooks';

type P = {
  canComment: boolean;
  canDeleteComment: (comment: Reply) => boolean;
  comments: Reply[];
  contentObjectId: string;
  contentObjectType: CommentableContentObject['type'];
  nested?: boolean;
  onRequestMentions: RichTextMentions;
  scrollToCommentId?: string;
  scrollToReplyId?: string;
  topParentShareableId?: string;
};

const COMMENTS_PER_PAGE = 3;

const CommentList = ({
  canComment,
  canDeleteComment,
  comments,
  contentObjectId,
  contentObjectType,
  nested,
  onRequestMentions,
  scrollToCommentId,
  scrollToReplyId,
  topParentShareableId,
}: P) => {
  const commentRef = useRef<HTMLDivElement>(null);
  const commentOrReplyId = useMemo(() => scrollToCommentId ?? scrollToReplyId, [scrollToCommentId, scrollToReplyId]);
  const commentDisplayInitialCount = useCommentDisplayInitialCount({
    commentOrReplyId,
    comments,
    commentsPerPage: COMMENTS_PER_PAGE,
  });
  const [displayCount, setDisplayCount] = useState(commentDisplayInitialCount);

  const visibleComments = useMemo(() => {
    const startIndex = Math.max(0, comments.length - displayCount);
    return comments.slice(startIndex);
  }, [comments, displayCount]);

  const hasMoreComments = useMemo(() => comments.length > displayCount, [comments.length, displayCount]);

  const handleShowMore = useCallback(() => {
    setDisplayCount((previous) => previous + COMMENTS_PER_PAGE);
  }, [setDisplayCount]);

  useEffect(() => {
    const commentRefCurrent = commentRef.current;
    if (commentOrReplyId && commentRefCurrent) {
      window.requestAnimationFrame(() => {
        // TODO: this is hack, find better solution so we dont use fixed constants
        const navigationHeight = 64;
        const spacingHeight = 16;

        window.scrollTo({
          behavior: 'smooth',
          top: window.scrollY + commentRefCurrent.getBoundingClientRect().top - navigationHeight - spacingHeight,
        });
      });
    }
  }, [commentOrReplyId]);

  const scrollRef = useCallback(
    (comment: Reply) => {
      if (!commentOrReplyId || (scrollToCommentId && scrollToReplyId)) {
        return undefined;
      }
      return comment.id === commentOrReplyId ? commentRef : undefined;
    },
    [scrollToCommentId, scrollToReplyId, commentOrReplyId],
  );

  return (
    <ul className={cn('comment-list', { 'comment-list--nested': nested })}>
      <Flex gap="ms" vertical>
        {hasMoreComments ? (
          <li>
            <Button className="comment-list--load-more" onClick={handleShowMore} type="link-primary">
              Load previous comments
            </Button>
          </li>
        ) : null}
        {visibleComments.map((comment) => (
          <li key={comment.id}>
            <CommentThread
              canComment={canComment}
              canDeleteComment={canDeleteComment}
              comment={comment}
              contentObjectId={contentObjectId}
              contentObjectType={contentObjectType}
              onRequestMentions={onRequestMentions}
              scrollRef={scrollRef(comment)}
              scrollToReplyId={scrollToReplyId}
              topParentShareableId={topParentShareableId}
            />
          </li>
        ))}
      </Flex>
    </ul>
  );
};

export default CommentList;
